import { LbNotificationExpiry } from '@electron/shared/lb-notification.types';
import {
  buildSkipEntry,
  type MeetingAutoStartSkipReason,
} from '@electron/shared/meeting-debug.types';
import {
  type EventType,
  getMeetingLink,
  isDeclinedByUser,
  isSoloEvent,
  MeetingAutoStopTimeout,
  type MeetingHeartbeatHealth,
  type MeetingInterruptReason,
  type MeetingTranscriptionInputDevicesPayload,
  type StopNoteTakingInactivityTimeoutTelemetry,
  type StopNoteTakingPresenceLostCause,
  type StopNoteTakingPresenceLostTelemetry,
  withAuthUser,
} from '@electron/shared/meetings.types';
import {
  config,
  getSDK,
  getStores,
  hasSDK,
  MAX_AUTOMATIC_MEETING_PREPS_PER_PASS,
  useUserConfig,
  useStores,
} from '@littlebird/sdk';
import {
  type MeetingCircle,
  type MeetingCircles,
  withCircleHidden,
  withoutHiddenCircles,
} from '@littlebird/ui-utils';
import { useNavigate } from '@tanstack/react-router';
import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { minBy, uniq } from 'lodash';
import { computed, type IComputedValue } from 'mobx';
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { electronApis } from '@/features/electron-api';
import { meetingDebugStore } from '@/features/meeting-debug/meeting-debug.store';
import {
  alertUnexpectedRecordingStop,
  clearUnexpectedStop,
} from '@/features/meetings/meetings-unexpected-stop';
import { useAppShortcut } from '@/features/shortcuts';
import { withUIDelay } from '@/lib/animate';
import { logError } from '@/lib/errorLogging';
import { withTimeout } from '@/lib/promise';
import { userStore } from '@/stores/user.store';
import { AppCapability, inElectron, isCapable } from '@/utils/capabilities';
import { triggerDeepLink } from '@/utils/deepLink';

import { useAutomaticMeetingPrepEnabled } from './automatic-meeting-prep.hooks';
import { meetingAppDetections } from './meeting-app-detections';
import { meetingAppOffers } from './meeting-app-offers';
import {
  getDisplayAttendees,
  getOtherAttendees,
  storeAttendeesToDisplayAttendees,
  storeMeetingToMeeting,
} from './meeting-mapper';
import { formatAttendees } from './meetings-list.utils';
import { MEETING_PREP_READY_SEARCH } from './meetings-prep-visibility';
import {
  getSessionAppId,
  normalizeAppBundleId,
  noteDetectedMeetingApps,
} from './meetings-recording-continuity';
import { meetingsSidebarStore } from './meetings-sidebar.store';
import { findCalendarEventById, meetingApis } from './meetings.query';
import { getMeeting, meetingStore } from './meetings.state';
import {
  type CalendarEvent,
  getMeetingProvider,
  type Meeting,
  PLACEHOLDER_MEETING_TITLE,
} from './meetings.types';

export interface InactivityTimeoutInput {
  meetingId: string | undefined;
  timeoutMs: number;
  lastAudioReceivedAt: Date | undefined;
  lastTranscriptAt: Date | undefined;
  recordingStartedAt: Date | undefined;
  transcriptLengthAtStart: number;
  transcriptLengthAtTimeout: number;
  appName: string | undefined;
}

export function useMeetingNotesIntentPrefetch() {
  const hasPrefetchedMeetingNotesRef = useRef(false);
  const prefetchInFlightRef = useRef<Promise<unknown> | null>(null);

  return useCallback(() => {
    meetingApis.prefetchCalendarEvents();

    if (hasPrefetchedMeetingNotesRef.current || prefetchInFlightRef.current)
      return;
    if (!inElectron() || !isCapable(AppCapability.RecordMeeting)) return;

    prefetchInFlightRef.current = meetingApis
      .getMacPermissions({ silent: true, throwOnError: true })
      .then(() => {
        hasPrefetchedMeetingNotesRef.current = true;
      })
      .catch(() => {
        // Retry on next intent
      })
      .finally(() => {
        prefetchInFlightRef.current = null;
      });
  }, []);
}

function formatTime(date: Date | undefined): string {
  if (!date) return 'never';
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

function formatRelativeTime(
  date: Date | undefined,
  referenceDate: Date | undefined
): string {
  if (!date) return 'never';
  const time = formatTime(date);
  if (!referenceDate) return time;

  const diffMs = date.getTime() - referenceDate.getTime();
  return `${time} (+${formatDuration(diffMs)})`;
}

function formatIsoDate(date: Date | undefined): string | undefined {
  return date?.toISOString();
}

function secondsSince(
  earlierDate: Date | undefined,
  laterDate: Date
): number | undefined {
  if (!earlierDate) return undefined;
  return Math.max(0, (laterDate.getTime() - earlierDate.getTime()) / 1000);
}

export function buildInactivityTimeoutTelemetry(
  input: InactivityTimeoutInput,
  timeoutTriggeredAt: Date = new Date()
) {
  const {
    meetingId,
    timeoutMs,
    lastAudioReceivedAt,
    lastTranscriptAt,
    recordingStartedAt,
    transcriptLengthAtStart,
    transcriptLengthAtTimeout,
    appName,
  } = input;
  const transcriptGrowth = transcriptLengthAtTimeout - transcriptLengthAtStart;
  const timeoutMinutes = Math.round(timeoutMs / 60000);
  const meetingDurationMs = recordingStartedAt
    ? timeoutTriggeredAt.getTime() - recordingStartedAt.getTime()
    : 0;
  const secondsSinceLastTranscriptAtTimeout = secondsSince(
    lastTranscriptAt,
    timeoutTriggeredAt
  );
  const secondsSinceLastSpeechAudioAtTimeout = secondsSince(
    lastAudioReceivedAt,
    timeoutTriggeredAt
  );
  const transcriptGapSecondsAtTimeout =
    secondsSinceLastTranscriptAtTimeout !== undefined &&
    secondsSinceLastSpeechAudioAtTimeout !== undefined
      ? secondsSinceLastTranscriptAtTimeout -
        secondsSinceLastSpeechAudioAtTimeout
      : undefined;
  const inactivityTimeoutTelemetry: StopNoteTakingInactivityTimeoutTelemetry = {
    timeoutMinutes,
    timeoutTriggeredAt: timeoutTriggeredAt.toISOString(),
    lastTranscriptReceivedAt: formatIsoDate(lastTranscriptAt),
    secondsSinceLastTranscriptAtTimeout,
    lastSpeechAudioReceivedAt: formatIsoDate(lastAudioReceivedAt),
    secondsSinceLastSpeechAudioAtTimeout,
    transcriptGapSecondsAtTimeout,
  };

  return {
    logPayload: {
      meetingId,
      timeoutMinutes,
      recordingStartedAt: formatTime(recordingStartedAt),
      recordingStartedAtIso: formatIsoDate(recordingStartedAt),
      lastSpeechAudioReceivedAt: formatRelativeTime(
        lastAudioReceivedAt,
        recordingStartedAt
      ),
      lastSpeechAudioReceivedAtIso:
        inactivityTimeoutTelemetry.lastSpeechAudioReceivedAt,
      secondsSinceLastSpeechAudioAtTimeout,
      lastTranscriptAt: formatRelativeTime(
        lastTranscriptAt,
        recordingStartedAt
      ),
      lastTranscriptReceivedAtIso:
        inactivityTimeoutTelemetry.lastTranscriptReceivedAt,
      secondsSinceLastTranscriptAtTimeout,
      transcriptGapSecondsAtTimeout,
      timeoutTriggeredAt: inactivityTimeoutTelemetry.timeoutTriggeredAt,
      timedOutAt: formatRelativeTime(timeoutTriggeredAt, recordingStartedAt),
      transcriptLengthAtStart,
      transcriptLengthAtTimeout,
      transcriptGrowth,
      appName: appName,
      meetingDuration: formatDuration(meetingDurationMs),
    },

    notificationMessage: `Stopped due to ${timeoutMinutes} minutes of inactivity`,
    inactivityTimeoutTelemetry,
  };
}

export async function handleInactivityTimeout(input: InactivityTimeoutInput) {
  const timeoutTriggeredAt = new Date();
  const telemetry = buildInactivityTimeoutTelemetry(input, timeoutTriggeredAt);

  console.info('[inactivity-timeout]', telemetry.logPayload);

  await meetingApis.enqueueStopMeeting(
    {
      type: 'inactivity_timeout',
      telemetry: telemetry.inactivityTimeoutTelemetry,
    },
    {
      onStopped: ({ meetingId, meetingWasPersistedInBackend }) => {
        // Only a meeting the backend saved can be resumed
        electronApis.lbNotifications.sendNotification(
          meetingWasPersistedInBackend
            ? {
                id: uuidv4(),
                view: 'meeting-presence-lost',
                expiresAt: LbNotificationExpiry.alert(),
                payload: {
                  meetingId,
                  // The view's subtitle is one line, so it gets the short form
                  message: `Inactive for ${telemetry.inactivityTimeoutTelemetry.timeoutMinutes} minutes`,
                  stopReason: 'inactivity_timeout',
                },
                sound: 'listen-close',
                delivery: 'expedited',
              }
            : {
                id: uuidv4(),
                view: 'basic',
                expiresAt: LbNotificationExpiry.alert(),
                payload: {
                  title: 'Note-Taking Stopped',
                  message: telemetry.notificationMessage,
                },
                sound: 'listen-close',
                delivery: 'expedited',
              }
        );
      },
    }
  );
}

export function useAutoStopActiveMeeting(meetingId: string | undefined) {
  const autoStopTimeout = config.meetings.auto_stop_timeout;
  const isRecording = meetingStore.isMeetingIdRecording(meetingId);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastTranscriptReceivedAt = meetingStore.lastTranscriptReceivedAt;
  const timeoutMs = autoStopTimeout * 60 * 1000;

  const resetTimeout = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  };

  useEffect(() => {
    if (timeoutRef.current) resetTimeout();
    if (!isRecording) return;
    if (autoStopTimeout === MeetingAutoStopTimeout.Never) return;

    const now = Date.now();
    const lastTranscriptAt = lastTranscriptReceivedAt?.getTime() ?? now;
    const timeSinceLastTranscript = Math.max(0, now - lastTranscriptAt);
    const timeRemaining = timeoutMs - timeSinceLastTranscript;

    const triggerTimeout = async () => {
      if (!hasSDK()) return;

      await handleInactivityTimeout({
        meetingId: meetingId ?? undefined,
        timeoutMs,
        lastAudioReceivedAt: meetingStore.lastAudioReceivedAt,
        lastTranscriptAt: meetingStore.lastTranscriptReceivedAt,
        recordingStartedAt:
          meetingStore.asRecordingOrStoppingMeeting?.startedAt,
        transcriptLengthAtStart:
          meetingStore.asRecordingOrStoppingMeeting?.transcriptLengthAtStart ??
          0,
        transcriptLengthAtTimeout: meetingId
          ? meetingStore.getTranscriptLines(meetingId).join('\n').length
          : 0,
        appName: meetingId
          ? (getMeeting(meetingId)?.source_app ?? undefined)
          : undefined,
      });
    };

    if (timeRemaining <= 0) {
      triggerTimeout();
      return;
    }

    timeoutRef.current = setTimeout(() => {
      triggerTimeout();
      timeoutRef.current = undefined;
    }, timeRemaining);

    return resetTimeout;
  }, [
    meetingId,
    isRecording,
    lastTranscriptReceivedAt,
    timeoutMs,
    autoStopTimeout,
  ]);
}

const APP_BINDING_WINDOW_MS = 120_000;

type DetectedMeetingApp = {
  name?: string;
  bundleIdentifier?: string;
};

type PresenceLostDuringStartup = {
  meetingId: string | undefined;
  cause: StopNoteTakingPresenceLostCause;
  detectedApps: DetectedMeetingApp[];
};

type BindingBeforeFirstReport = {
  meetingId: string | undefined;
  appsAtBinding: readonly DetectedMeetingApp[];
};

function findAppMissingFrom(
  apps: readonly DetectedMeetingApp[],
  earlierApps: readonly DetectedMeetingApp[]
) {
  const earlierAppIds = new Set(
    earlierApps.map((app) => normalizeAppBundleId(getSessionAppId(app)))
  );
  return apps.find(
    (app) => !earlierAppIds.has(normalizeAppBundleId(getSessionAppId(app)))
  );
}

function clearAppBindingTimeout(
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
) {
  if (!timeoutRef.current) return;
  clearTimeout(timeoutRef.current);
  timeoutRef.current = null;
}

function closeAppBindingWindow(
  windowOpenRef: MutableRefObject<boolean>,
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
) {
  windowOpenRef.current = false;
  clearAppBindingTimeout(timeoutRef);
}

function bindDetectedAppToMeeting(
  meetingId: string | undefined,
  appBundleId: string
) {
  meetingStore.setRecordingSessionAppBundleId(appBundleId);

  console.info('[session-app-bound]', { meetingId, appBundleId });
  meetingDebugStore.record({
    kind: 'app_bound',
    meetingId,
    summary: `Bound to ${appBundleId}`,
    fields: { appBundleId },
  });

  if (!meetingId) return;

  const update = {
    id: meetingId,
    source_app: appBundleId,
  };
  meetingStore.setMeeting(update);
  meetingApis.updateMeeting(update);
}

function tryBindDetectedAppToMeeting(
  meetingId: string | undefined,
  detectedApps: readonly DetectedMeetingApp[],
  windowOpenRef: MutableRefObject<boolean>,
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
) {
  const bindingIsOpen =
    windowOpenRef.current || meetingStore.recordingSessionExpectsCall;
  if (!bindingIsOpen) return false;

  const firstApp = detectedApps[0];
  const firstAppId = firstApp && getSessionAppId(firstApp);
  if (!firstAppId) return false;

  bindDetectedAppToMeeting(meetingId, firstAppId);
  closeAppBindingWindow(windowOpenRef, timeoutRef);

  return true;
}

function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

function buildPresenceLostTelemetry(input: {
  meetingId: string;
  cause: StopNoteTakingPresenceLostCause;
  detectedApps: DetectedMeetingApp[];
  stopTriggeredAt: Date;
}): StopNoteTakingPresenceLostTelemetry {
  const { meetingId, cause, detectedApps, stopTriggeredAt } = input;
  const eventId = getMeeting(meetingId)?.event_id;
  const scheduledEnd = eventId
    ? findCalendarEventById(eventId)?.end_datetime
    : undefined;
  return {
    cause,
    stopTriggeredAt: stopTriggeredAt.toISOString(),
    detectedAppBundleIds: detectedApps.flatMap((app) =>
      app.bundleIdentifier ? [app.bundleIdentifier] : []
    ),
    secondsSinceLastTranscriptAtStop: secondsSince(
      meetingStore.lastTranscriptReceivedAt,
      stopTriggeredAt
    ),
    secondsSinceLastSpeechAudioAtStop: secondsSince(
      meetingStore.lastAudioReceivedAt,
      stopTriggeredAt
    ),
    scheduledEndAt: scheduledEnd
      ? new Date(scheduledEnd).toISOString()
      : undefined,
  };
}

type MeetingInputDeviceNotificationScope = {
  meetingId: string;
  deviceIds: Set<string>;
};

export function useMeetingInputDeviceConnectedNotification() {
  const recording = meetingStore.recording;
  const activeMeetingId =
    recording.status === 'recording' ? recording.meetingId : undefined;
  const activeMeetingIdRef = useLatestRef(activeMeetingId);
  const knownDevicesStateRef =
    useRef<MeetingTranscriptionInputDevicesPayload | null>(null);
  const notifiedDevicesRef = useRef<MeetingInputDeviceNotificationScope | null>(
    null
  );
  const getNotifiedDeviceIdsForMeeting = useCallback((meetingId: string) => {
    if (notifiedDevicesRef.current?.meetingId !== meetingId) {
      notifiedDevicesRef.current = {
        meetingId,
        deviceIds: new Set(),
      };
    }

    return notifiedDevicesRef.current.deviceIds;
  }, []);

  useEffect(() => {
    knownDevicesStateRef.current = null;

    if (!activeMeetingId) {
      return;
    }

    let isCancelled = false;
    electronApis.transcriptionInputDevice
      .getDevices()
      .then((devicesState) => {
        if (isCancelled || activeMeetingIdRef.current !== activeMeetingId) {
          return;
        }

        // A device-change event may have raced ahead of getDevices(); if a
        // newer baseline already exists, don't overwrite it with the stale snapshot
        if (knownDevicesStateRef.current) {
          return;
        }

        knownDevicesStateRef.current = devicesState;
      })
      .catch((error) => {
        logError(
          error,
          '[meeting-input-device-connected] Failed to load initial device snapshot'
        );
      });

    return () => {
      isCancelled = true;
    };
  }, [activeMeetingId, activeMeetingIdRef]);

  useEffect(() => {
    return electronApis.transcriptionInputDevice.onDeviceChanged(
      async (payload) => {
        const meetingId = activeMeetingIdRef.current;
        if (!meetingId) {
          knownDevicesStateRef.current = payload;
          return;
        }

        const previousDevicesState = knownDevicesStateRef.current;
        knownDevicesStateRef.current = payload;

        if (!previousDevicesState) {
          return;
        }

        const previousDeviceIds = new Set(
          previousDevicesState.availableDevices.map((device) => device.id)
        );
        const newlyConnectedDevices = payload.availableDevices.filter(
          (device) => !previousDeviceIds.has(device.id)
        );
        if (newlyConnectedDevices.length === 0) {
          return;
        }

        const notifiedDeviceIds = getNotifiedDeviceIdsForMeeting(meetingId);
        for (const newlyConnectedDevice of newlyConnectedDevices) {
          if (notifiedDeviceIds.has(newlyConnectedDevice.id)) {
            continue;
          }

          // Skip when the newly connected device is already the effective input
          // (system auto-switched to it; no point asking the user to switch)
          if (payload.effectiveCurrentDevice?.id === newlyConnectedDevice.id) {
            continue;
          }

          const notificationId = `meeting-input-device-connected:${meetingId}:${newlyConnectedDevice.id}`;

          try {
            const alreadyPending =
              await electronApis.lbNotifications.hasNotification(
                notificationId
              );
            if (alreadyPending) {
              notifiedDeviceIds.add(newlyConnectedDevice.id);
              continue;
            }

            await electronApis.lbNotifications.sendNotification({
              id: notificationId,
              view: 'meeting-input-device-connected',
              expiresAt: LbNotificationExpiry.alert(),
              payload: {
                meetingId,
                deviceId: newlyConnectedDevice.id,
                deviceName: newlyConnectedDevice.name,
              },
              sound: 'ask',
            });

            notifiedDeviceIds.add(newlyConnectedDevice.id);
          } catch (error) {
            logError(
              error,
              '[meeting-input-device-connected] Failed to send input-device notification'
            );
          }
        }
      }
    );
  }, [activeMeetingIdRef, getNotifiedDeviceIdsForMeeting]);
}

function stopMeetingForPresenceLoss(
  meetingId: string,
  cause: StopNoteTakingPresenceLostCause,
  detectedApps: DetectedMeetingApp[]
) {
  if (meetingAppOffers.presenceLossStop.status === 'stopping') return;

  meetingAppOffers.presenceLossStop = { status: 'stopping' };

  const recording = meetingStore.recording;
  if (recording.status !== 'recording' || recording.meetingId !== meetingId) {
    return;
  }

  const telemetry = buildPresenceLostTelemetry({
    meetingId,
    cause,
    detectedApps,
    stopTriggeredAt: new Date(),
  });
  console.info('[presence-lost] stopping', { meetingId, ...telemetry });

  meetingApis
    .enqueueStopMeeting(
      { type: 'meeting_presence_lost', telemetry },
      {
        onStopped: ({
          meetingId: stoppedMeetingId,
          meetingWasPersistedInBackend,
        }) => {
          const notification = meetingWasPersistedInBackend
            ? {
                id: uuidv4(),
                view: 'meeting-presence-lost' as const,
                expiresAt: LbNotificationExpiry.alert(),
                payload: {
                  meetingId: stoppedMeetingId,
                  stopReason: 'meeting_presence_lost' as const,
                },
                sound: 'problem' as const,
                delivery: 'expedited' as const,
              }
            : {
                id: uuidv4(),
                view: 'basic' as const,
                expiresAt: LbNotificationExpiry.alert(),
                payload: {
                  title: 'Recording stopped',
                  message: 'Meeting app no longer detected',
                },
                sound: 'problem' as const,
                delivery: 'expedited' as const,
              };

          meetingAppOffers.presenceLossStop = {
            status: 'notifying',
            notificationId: notification.id,
            sendInFlight: true,
          };

          electronApis.lbNotifications
            .sendNotification(notification)
            .catch((error) => {
              if (
                meetingAppOffers.endPresenceLossNoticeIfCurrent(notification.id)
              ) {
                void meetingAppOffers.offerNext(undefined);
              }

              logError(
                error,
                '[meeting-app-detection] Failed to send presence-loss notification'
              );
            })
            .finally(() => {
              const presenceLossStop = meetingAppOffers.presenceLossStop;
              if (
                presenceLossStop.status === 'notifying' &&
                presenceLossStop.notificationId === notification.id &&
                presenceLossStop.sendInFlight
              ) {
                meetingAppOffers.presenceLossStop = {
                  ...presenceLossStop,
                  sendInFlight: false,
                };
                meetingAppOffers.resumeOffersAfterPresenceLossNotice(
                  notification.id
                );
              }
            });
        },
      }
    )
    .catch((error) => {
      logError(
        error,
        '[meeting-app-detection] Failed to auto-stop meeting after app ended'
      );
    })
    .finally(() => {
      if (meetingAppOffers.presenceLossStop.status === 'stopping') {
        meetingAppOffers.presenceLossStop = { status: 'idle' };
        void meetingAppOffers.offerNext(undefined);
      }
    });
}

export function useMeetingAppDetection() {
  // React Compiler would cache the MobX store reads below and miss their changes
  'use no memo';
  const recording = meetingStore.recording;
  const recordingStatus = recording.status;
  const isRecordingOrLoading =
    recordingStatus === 'loading' || recordingStatus === 'recording';
  const activeMeetingId =
    recordingStatus === 'idle' ? undefined : recording.meetingId;
  const recordingSessionAppBundleId = meetingStore.recordingSessionAppBundleId;

  const appBindingWindowOpenRef = useRef(false);
  const appBindingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const bindingBeforeFirstReportRef = useRef<
    BindingBeforeFirstReport | undefined
  >(undefined);
  const presenceLostDuringStartupRef = useRef<
    PresenceLostDuringStartup | undefined
  >(undefined);
  useEffect(() => {
    if (recordingStatus === 'idle') void meetingAppOffers.offerNext(undefined);
  }, [recordingStatus]);

  useEffect(() => {
    const resumeIsBoundToStoredApp =
      meetingStore.recordingSessionStartReason === 'resume' &&
      !!meetingStore.recordingSessionAppBundleId;
    if (isRecordingOrLoading && resumeIsBoundToStoredApp) {
      bindingBeforeFirstReportRef.current = {
        meetingId: activeMeetingId,
        appsAtBinding: [],
      };
    }
  }, [isRecordingOrLoading, activeMeetingId]);

  // Manage the 120s binding window for active sessions missing a bound app
  useEffect(() => {
    clearAppBindingTimeout(appBindingTimeoutRef);
    if (!isRecordingOrLoading) {
      bindingBeforeFirstReportRef.current = undefined;
      presenceLostDuringStartupRef.current = undefined;
    }

    if (isRecordingOrLoading && !recordingSessionAppBundleId) {
      appBindingWindowOpenRef.current = true;

      if (
        tryBindDetectedAppToMeeting(
          activeMeetingId,
          meetingAppDetections.currentApps,
          appBindingWindowOpenRef,
          appBindingTimeoutRef
        )
      ) {
        bindingBeforeFirstReportRef.current = {
          meetingId: activeMeetingId,
          appsAtBinding: meetingAppDetections.currentApps,
        };
        return () => {
          clearAppBindingTimeout(appBindingTimeoutRef);
        };
      }

      appBindingTimeoutRef.current = setTimeout(() => {
        closeAppBindingWindow(appBindingWindowOpenRef, appBindingTimeoutRef);
      }, APP_BINDING_WINDOW_MS);
    } else {
      appBindingWindowOpenRef.current = false;
    }

    return () => {
      clearAppBindingTimeout(appBindingTimeoutRef);
    };
  }, [isRecordingOrLoading, activeMeetingId, recordingSessionAppBundleId]);

  useEffect(() => {
    const presenceLostDuringStartup = presenceLostDuringStartupRef.current;
    const presenceWasLostDuringStartup =
      recordingStatus === 'recording' &&
      !!activeMeetingId &&
      presenceLostDuringStartup?.meetingId === activeMeetingId;
    if (!presenceWasLostDuringStartup) return;

    presenceLostDuringStartupRef.current = undefined;
    stopMeetingForPresenceLoss(
      activeMeetingId,
      presenceLostDuringStartup.cause,
      presenceLostDuringStartup.detectedApps
    );
  }, [recordingStatus, activeMeetingId]);

  useEffect(() => {
    return meetingAppOffers.connectDetectionHook(({ apps, detectionId }) => {
      const latestRecording = meetingStore.recording;
      const recordingStatus = latestRecording.status;
      const recordingSessionAppBundleId =
        meetingStore.recordingSessionAppBundleId;
      const sessionIsStartingOrRecording =
        recordingStatus === 'loading' || recordingStatus === 'recording';
      noteDetectedMeetingApps(
        apps,
        latestRecording.status === 'recording'
          ? {
              meetingId: latestRecording.meetingId,
              appBundleId: recordingSessionAppBundleId,
            }
          : undefined
      );

      if (sessionIsStartingOrRecording) {
        const meetingId = latestRecording.meetingId;
        const normalizedSessionAppBundleId = normalizeAppBundleId(
          recordingSessionAppBundleId
        );

        if (!normalizedSessionAppBundleId) {
          if (
            tryBindDetectedAppToMeeting(
              meetingId,
              apps,
              appBindingWindowOpenRef,
              appBindingTimeoutRef
            )
          ) {
            // The bound app is in the current apps list, so no auto-stop needed
            return;
          }
          return;
        }
        presenceLostDuringStartupRef.current = undefined;
        if (apps.length === 0) bindingBeforeFirstReportRef.current = undefined;

        if (apps.length > 0) {
          const boundAppIsStillDetected = apps.some(
            (app) =>
              normalizeAppBundleId(getSessionAppId(app)) ===
              normalizedSessionAppBundleId
          );
          const bindingBeforeFirstReport =
            bindingBeforeFirstReportRef.current?.meetingId === meetingId
              ? bindingBeforeFirstReportRef.current
              : undefined;
          bindingBeforeFirstReportRef.current = undefined;
          if (boundAppIsStillDetected) return;

          const newlyReportedApp =
            bindingBeforeFirstReport &&
            findAppMissingFrom(apps, bindingBeforeFirstReport.appsAtBinding);
          const newlyReportedAppId =
            newlyReportedApp && getSessionAppId(newlyReportedApp);
          if (newlyReportedAppId) {
            bindDetectedAppToMeeting(meetingId, newlyReportedAppId);
            return;
          }
        }
        const cause =
          apps.length === 0 ? 'no_apps_detected' : 'bound_app_missing';
        if (recordingStatus !== 'recording') {
          presenceLostDuringStartupRef.current = {
            meetingId,
            cause,
            detectedApps: apps,
          };
          return;
        }
        if (!meetingId) return;

        stopMeetingForPresenceLoss(meetingId, cause, apps);
        return;
      }

      const skip = (reason: MeetingAutoStartSkipReason) =>
        meetingDebugStore.record({
          ...buildSkipEntry('app_detection', reason, { recordingStatus }),
          meetingId: null,
          detectionId,
        });
      if (recordingStatus !== 'idle') {
        skip('already_recording');
        return;
      }

      if (apps.length === 0) {
        skip('no_apps_detected');
        return;
      }

      void meetingAppOffers.offerNext(detectionId);
    });
  }, []);
}

export function useMeetingHotkey() {
  const isLoading = meetingStore.controlBarLoading;

  const startOrStopMeeting = useCallback(() => {
    meetingStore.controlBarLoading = true;

    withUIDelay(async () => {
      const meetingInViewId = meetingStore.meetingInViewId;
      const r = meetingStore.recording;
      const meetingId =
        meetingInViewId ?? meetingStore.asRecordingOrStoppingMeeting?.meetingId;
      try {
        switch (r.status) {
          case 'recording':
            meetingStore.closeTranscriptPopover();
            await meetingApis.enqueueStopMeeting(
              { type: 'stop_note_taking' },
              { deferSummary: true }
            );
            break;
          case 'idle': {
            const isPreparedMeetingFirstRecording =
              !!meetingId && meetingStore.isPrepState(meetingId);
            await meetingApis.enqueueStartMeeting({
              payload: meetingId
                ? {
                    meetingId,
                    startReason: 'resume',
                    resumeSource: isPreparedMeetingFirstRecording
                      ? undefined
                      : 'shortcut',
                  }
                : { startReason: 'manual' },
            });
            break;
          }
        }
      } finally {
        meetingStore.controlBarLoading = false;
      }
    });
  }, []);

  useAppShortcut(
    'meetings.toggle',
    startOrStopMeeting,
    {
      enabled: !isLoading,
      enableOnContentEditable: true,
      enableOnFormTags: true,
      preventDefault: true,
    },
    [isLoading]
  );
}

export const UPCOMING_DAYS_WINDOW = 7;
const AUTOMATIC_MEETING_PREP_CHECK_INTERVAL_MS = 60 * 1000;
type AutomaticMeetingPrepRequestSet = Set<string>;

export interface UpcomingMeetingWithStatus {
  event: CalendarEvent;
  meeting?: Meeting;
}

function getAutomaticMeetingPrepKey(event: CalendarEvent): string {
  return `${event.id}:${event.start_datetime}`;
}

function pruneAutomaticMeetingPrepRequests(
  requestedEventKeys: AutomaticMeetingPrepRequestSet,
  calendarEvents: CalendarEvent[],
  nowMs: number
) {
  const activeEventKeys = new Set(
    calendarEvents.flatMap((event) => {
      const endMs = new Date(event.end_datetime).getTime();
      if (!Number.isFinite(endMs) || endMs <= nowMs) return [];
      return [getAutomaticMeetingPrepKey(event)];
    })
  );

  for (const eventKey of requestedEventKeys.keys()) {
    if (!activeEventKeys.has(eventKey)) {
      requestedEventKeys.delete(eventKey);
    }
  }
}

function shouldAutomaticallyPrepMeeting(
  event: CalendarEvent,
  nowMs: number
): boolean {
  const startMs = new Date(event.start_datetime).getTime();
  const endMs = new Date(event.end_datetime).getTime();

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;
  if (event.status === 'cancelled') return false;
  if (config.meetings.exclude_solo_events && isSoloEvent(event)) return false;
  if (isDeclinedByUser(event)) return false;

  const leadTimeMs = config.meetings.meeting_prep_lead_time * 60 * 1000;
  return startMs > nowMs && startMs <= nowMs + leadTimeMs;
}

function notifyAutomaticMeetingPrepReady(meetingId: string, title: string) {
  if (!inElectron()) return;

  void electronApis.lbNotifications.sendNotification({
    id: uuidv4(),
    view: 'basic',
    sound: 'done',
    transient: true,
    expiresAt: LbNotificationExpiry.alert(),
    payload: {
      title,
      message: 'Meeting prep is ready',
      route: {
        to: '/meetings/$meetingId',
        params: { meetingId },
        search: MEETING_PREP_READY_SEARCH,
      },
    },
  });
}

async function requestAutomaticMeetingPrep(
  event: CalendarEvent,
  requestedEventKeys: AutomaticMeetingPrepRequestSet
) {
  const eventKey = getAutomaticMeetingPrepKey(event);

  try {
    const meetings = getStores().meetings;
    const existingMeeting = await meetings.findByEventId(event.id);

    if (existingMeeting?.prepStatus === 'skipped') return;

    meetings.requestPrepContent(event.id, {
      skipIfCompleted: true,
      automatic: true,
      onCompleted: (meeting) => {
        notifyAutomaticMeetingPrepReady(
          meeting.id,
          meeting.name || event.summary
        );
      },
      onAlreadyCompleted: () => undefined,
      onSkipped: () => undefined,
      onRequestFailed: () => {
        requestedEventKeys.delete(eventKey);
      },
      onFailed: () => {
        requestedEventKeys.delete(eventKey);
      },
    });
  } catch (error) {
    requestedEventKeys.delete(eventKey);
    logError(error, 'Failed to request automatic meeting prep');
  }
}

export function useAutomaticMeetingPrep() {
  const automaticMeetingPrepEnabled = useAutomaticMeetingPrepEnabled();
  const [nowMs, setNowMs] = useState(Date.now);
  const queryEnabled = inElectron() && automaticMeetingPrepEnabled && hasSDK();
  const { data: calendarEvents = [] } = meetingApis.useCalendarEventsQuery({
    enabled: queryEnabled,
    // The check timer below keeps running while the window is hidden,
    // so the list has to keep refreshing there too
    refetchIntervalInBackground: true,
  });
  const requestedEventKeysRef = useRef<AutomaticMeetingPrepRequestSet>(
    new Set()
  );

  useEffect(() => {
    if (!queryEnabled) return;

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, AUTOMATIC_MEETING_PREP_CHECK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [queryEnabled]);

  useEffect(() => {
    if (!queryEnabled) return;
    // Briefs can't be taken back, so none are requested from settings the server hasn't confirmed
    if (getSDK().userConfig.awaitingServerCopy) return;

    pruneAutomaticMeetingPrepRequests(
      requestedEventKeysRef.current,
      calendarEvents,
      nowMs
    );

    const soonestEventsToPrep = calendarEvents
      .filter(
        (event) =>
          shouldAutomaticallyPrepMeeting(event, nowMs) &&
          !requestedEventKeysRef.current.has(getAutomaticMeetingPrepKey(event))
      )
      .sort(
        (a, b) => Date.parse(a.start_datetime) - Date.parse(b.start_datetime)
      )
      .slice(0, MAX_AUTOMATIC_MEETING_PREPS_PER_PASS);

    for (const event of soonestEventsToPrep) {
      requestedEventKeysRef.current.add(getAutomaticMeetingPrepKey(event));
      void requestAutomaticMeetingPrep(event, requestedEventKeysRef.current);
    }
  }, [calendarEvents, nowMs, queryEnabled]);
}

const NEW_EVENT_CHECK_INTERVAL_MS = 60 * 1000;
export const NEW_EVENT_START_DELAY_MS = 5 * 60 * 1000;

function getNewEventPromptKey(event: CalendarEvent): string {
  return `${event.id}:${event.start_datetime}`;
}

function getNewEventPromptSkipReason(
  event: CalendarEvent,
  recordingEventId: string | undefined
): MeetingAutoStartSkipReason | undefined {
  const excludedEventTypes = config.calendar
    .excluded_event_types as EventType[];

  if (event.id === recordingEventId) return 'same_as_recording_event';
  if (event.status === 'cancelled') return 'cancelled_event';
  if (event.eventType && excludedEventTypes.includes(event.eventType))
    return 'excluded_event_type';
  if (config.meetings.exclude_solo_events && isSoloEvent(event))
    return 'solo_event';
  if (isDeclinedByUser(event)) return 'declined_event';
  return undefined;
}

function hasEventStartedDuringRecording(
  event: CalendarEvent,
  recordingStartedAtMs: number,
  nowMs: number
): boolean {
  const startMs = new Date(event.start_datetime).getTime();
  const endMs = new Date(event.end_datetime).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;

  return (
    startMs >= recordingStartedAtMs &&
    endMs > nowMs &&
    nowMs - startMs >= NEW_EVENT_START_DELAY_MS
  );
}

export function useMeetingNewEventStartedPrompt() {
  const recording = meetingStore.recording;
  const recordingMeetingId =
    recording.status === 'recording' ? recording.meetingId : undefined;
  const recordingStartedAtMs =
    recording.status === 'recording'
      ? recording.startedAt.getTime()
      : undefined;
  const isRecording = recordingMeetingId !== undefined;
  const [nowMs, setNowMs] = useState(Date.now);
  const { data: calendarEvents = [] } = meetingApis.useCalendarEventsQuery({
    enabled: isRecording,
  });
  const promptedEventKeysRef = useRef<Set<string>>(new Set());
  const recordedSkipKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    promptedEventKeysRef.current.clear();
    recordedSkipKeysRef.current.clear();
  }, [recordingMeetingId]);

  useEffect(() => {
    if (!isRecording) return;

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, NEW_EVENT_CHECK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isRecording]);

  useEffect(() => {
    if (!recordingMeetingId || recordingStartedAtMs === undefined) return;

    const recordingEventId =
      getMeeting(recordingMeetingId)?.event_id ?? undefined;
    const startedEvents = calendarEvents.filter(
      (event) =>
        !promptedEventKeysRef.current.has(getNewEventPromptKey(event)) &&
        hasEventStartedDuringRecording(event, recordingStartedAtMs, nowMs)
    );
    for (const event of startedEvents) {
      const skipReason = getNewEventPromptSkipReason(event, recordingEventId);
      const skipKey = `${getNewEventPromptKey(event)}:${skipReason}`;
      // The check reruns on a timer, so each skip is recorded once per recording
      if (!skipReason || recordedSkipKeysRef.current.has(skipKey)) continue;
      recordedSkipKeysRef.current.add(skipKey);
      meetingDebugStore.record({
        ...buildSkipEntry('new_event_prompt', skipReason, {
          eventId: event.id,
          eventTitle: event.summary,
        }),
        meetingId: recordingMeetingId,
      });
    }
    const startedEvent = startedEvents
      .filter(
        (event) =>
          getNewEventPromptSkipReason(event, recordingEventId) === undefined
      )
      .sort(
        (a, b) =>
          new Date(a.start_datetime).getTime() -
          new Date(b.start_datetime).getTime()
      )[0];
    if (!startedEvent) return;

    const eventKey = getNewEventPromptKey(startedEvent);
    promptedEventKeysRef.current.add(eventKey);

    const notificationId = `meeting-new-event-started:${recordingMeetingId}:${eventKey}`;

    void (async () => {
      try {
        if (
          await electronApis.lbNotifications.hasNotification(notificationId)
        ) {
          meetingDebugStore.record({
            ...buildSkipEntry(
              'new_event_prompt',
              'notification_already_shown',
              {
                eventId: startedEvent.id,
              }
            ),
            meetingId: recordingMeetingId,
          });
          return;
        }

        await electronApis.lbNotifications.sendNotification({
          id: notificationId,
          view: 'meeting-new-event-started',
          expiresAt: LbNotificationExpiry.alert(),
          payload: {
            eventId: startedEvent.id,
            eventTitle: startedEvent.summary,
            provider: getMeetingProvider(startedEvent),
            precedingMeetingId: recordingMeetingId,
          },
          sound: 'ask',
        });
      } catch (error) {
        promptedEventKeysRef.current.delete(eventKey);
        logError(
          error,
          '[meeting-new-event-started] Failed to send new-event notification'
        );
      }
    })();
  }, [calendarEvents, nowMs, recordingMeetingId, recordingStartedAtMs]);
}

const buildUpcomingMeetingsWithStatus = (
  calendarEvents: CalendarEvent[],
  now = new Date()
): UpcomingMeetingWithStatus[] => {
  const excludedEventTypes = config.calendar
    .excluded_event_types as EventType[];
  const meetingsStore = getStores().meetings;
  const recordingMeetingId =
    meetingStore.asRecordingOrStoppingMeeting?.meetingId;

  const futureDate = addDays(now, UPCOMING_DAYS_WINDOW);

  const recordingMeeting = recordingMeetingId
    ? meetingsStore.get(recordingMeetingId)
    : null;
  const recordingEventId = recordingMeeting?.eventId;

  const upcomingEvents = calendarEvents.filter((event) => {
    const startDate = new Date(event.start_datetime);
    const endDate = new Date(event.end_datetime);

    if (event.eventType && excludedEventTypes.includes(event.eventType))
      return false;

    if (isDeclinedByUser(event)) return false;

    return (
      endDate >= now &&
      startDate <= futureDate &&
      event.status !== 'cancelled' &&
      event.id !== recordingEventId
    );
  });

  return upcomingEvents
    .map((event) => {
      return {
        event,
        meeting: storeMeetingToMeeting(
          meetingsStore.getMeetingByEventId(event.id)
        ),
      };
    })
    .sort((a, b) => {
      const dateA = new Date(a.event.start_datetime);
      const dateB = new Date(b.event.start_datetime);
      return dateA.getTime() - dateB.getTime();
    });
};

export function useUpcomingCalendarEvents(options?: {
  excludeSoloEvents?: boolean;
}) {
  const { data: calendarEvents, isLoading } =
    meetingApis.useCalendarEventsQuery({
      excludeSoloEvents: options?.excludeSoloEvents,
    });

  if (!hasSDK()) {
    return { upcomingMeetingsWithStatus: [], isLoading };
  }

  return {
    upcomingMeetingsWithStatus: calendarEvents
      ? buildUpcomingMeetingsWithStatus(calendarEvents)
      : [],
    isLoading,
  };
}

export function useUpcomingMeetings() {
  const { upcomingMeetingsWithStatus, isLoading } = useUpcomingCalendarEvents();
  const excludeSoloEvents = config.meetings.exclude_solo_events;

  if (!excludeSoloEvents) {
    return { upcomingMeetingsWithStatus, isLoading };
  }

  return {
    upcomingMeetingsWithStatus: upcomingMeetingsWithStatus.filter(
      ({ event }) => !isSoloEvent(event)
    ),
    isLoading,
  };
}

// Discriminated union for crash recovery state
type CrashRecoveryState =
  | { status: 'idle' }
  | { status: 'handling'; meetingId: string | null; crashedAt: Date };

const CRASH_NOTIFICATION_THRESHOLD_MS = 2000;
const CONTEXTKIT_RESTART_GRACE_MS = 10_000;

/**
 * Resets meeting recording state when ContextKit crashes.
 * ContextKit handles audio capture; if it crashes mid-recording,
 * the UI must reflect that recording has stopped.
 *
 * Also handles automatic recovery: when ContextKit restarts after a crash,
 * attempts to resume the meeting that was interrupted.
 *
 * Notification logic:
 * - No notification shown on crash (wait to see if recovery is fast)
 * - If recovery takes > 2 seconds: shows "missed some data" notification
 * - If recovery fails: shows "Could Not Resume Recording" notification
 * - If recovery is fast (< 2 seconds): no notification (seamless recovery)
 */
export function useContextKitCrashRecovery() {
  const crashRecoveryRef = useRef<CrashRecoveryState>({ status: 'idle' });
  const crashAlertTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const recordingStatus = meetingStore.recording.status;

  // Reset when new recording starts
  useEffect(() => {
    if (recordingStatus === 'recording') {
      crashRecoveryRef.current = { status: 'idle' };
    }
  }, [recordingStatus]);

  // Handle crash - stop meeting and store ID for recovery
  useEffect(() => {
    const unlisten = electronApis.context.listenForContextKitDidCrash(() => {
      meetingDebugStore.record({
        kind: 'contextkit_crash',
        summary: 'ContextKit crashed',
        fields: { recordingStatus: meetingStore.recording.status },
      });
      if (meetingStore.recording.status !== 'recording') return;
      if (crashRecoveryRef.current.status === 'handling') return;

      crashRecoveryRef.current = {
        status: 'handling',
        meetingId: meetingStore.recording.meetingId,
        crashedAt: new Date(),
      };

      const errorMessage = 'ContextKit crashed, stopping meeting';
      logError(new Error(errorMessage), errorMessage);

      meetingApis.enqueueStopMeeting({ type: 'contextkit-crash' });

      const crashedMeetingId = meetingStore.recording.meetingId;
      clearTimeout(crashAlertTimerRef.current);
      crashAlertTimerRef.current = setTimeout(() => {
        if (meetingStore.isMeetingIdRecording(crashedMeetingId)) return;
        alertUnexpectedRecordingStop({
          meetingId: crashedMeetingId,
          cause: 'interrupted',
          title: 'Recording Stopped',
          message:
            'Recording stopped because the recording service stopped unexpectedly. Resume to keep recording.',
        });
      }, CONTEXTKIT_RESTART_GRACE_MS);
    });

    return () => {
      unlisten();
      clearTimeout(crashAlertTimerRef.current);
    };
  }, []);

  // Handle recovery - attempt to auto-resume with conditional notification
  useEffect(() => {
    const unlisten = electronApis.context.listenForContextKitDidRecover(
      async () => {
        const state = crashRecoveryRef.current;
        meetingDebugStore.record({
          kind: 'contextkit_crash',
          meetingId: state.status === 'handling' ? state.meetingId : undefined,
          summary: 'ContextKit recovered',
          fields: { recoveryStatus: state.status },
        });

        crashRecoveryRef.current = { status: 'idle' };

        const meetingIdToResume =
          state.status === 'handling' ? state.meetingId : undefined;
        const skip = (reason: MeetingAutoStartSkipReason) =>
          meetingDebugStore.record({
            ...buildSkipEntry('resume', reason),
            meetingId: meetingIdToResume ?? null,
          });

        if (state.status === 'handling') {
          clearTimeout(crashAlertTimerRef.current);
        }

        if (state.status !== 'handling' || !meetingIdToResume) {
          skip('nothing_to_resume');
          return;
        }

        const { crashedAt } = state;

        // Don't resume if a meeting is already active (user may have started one manually)
        if (meetingStore.recording.status === 'recording') {
          skip('already_recording');
          return;
        }

        const meeting = getMeeting(meetingIdToResume);
        if (!meeting) {
          skip('meeting_not_found');
          return;
        }

        const elapsedMs = Date.now() - crashedAt.getTime();
        const shouldNotify = elapsedMs > CRASH_NOTIFICATION_THRESHOLD_MS;

        try {
          await meetingApis.enqueueStartMeeting({
            payload: {
              meetingId: meetingIdToResume,
              startReason: 'resume',
              resumeSource: 'contextkit_recovery',
            },
          });

          if (!meetingStore.isMeetingIdRecording(meetingIdToResume)) {
            throw new Error(
              'ContextKit recovery did not resume the interrupted meeting'
            );
          }
          clearUnexpectedStop(meetingIdToResume);

          if (shouldNotify) {
            electronApis.lbNotifications.sendNotification({
              id: uuidv4(),
              view: 'basic',
              sound: 'attention',
              expiresAt: LbNotificationExpiry.alert(),
              payload: {
                title: 'Recording Resumed',
                message:
                  'Some audio may have been missed during a brief interruption',
                route: {
                  to: '/meetings/$meetingId',
                  params: { meetingId: meetingIdToResume },
                },
              },
            });
          }
        } catch (error) {
          logError(error, 'Failed to auto-resume meeting after crash recovery');

          alertUnexpectedRecordingStop({
            meetingId: meetingIdToResume,
            cause: 'auto_resume_failed',
            title: 'Could Not Resume Recording',
            message:
              "Recording stopped, and Littlebird couldn't restart it automatically. Resume to keep recording.",
          });
        }
      }
    );

    return unlisten;
  }, []);
}

const INTERRUPT_REASON_MESSAGES: Record<
  MeetingInterruptReason,
  { title: string; message: string }
> = {
  token_refresh_exhausted: {
    title: 'Recording Stopped',
    message:
      'Recording stopped because authentication failed after multiple attempts. Resume to keep recording.',
  },
  websocket_reconnect_exhausted: {
    title: 'Recording Stopped',
    message:
      "Recording stopped because the connection was lost and couldn't be restored. Resume to keep recording.",
  },
  audio_start_failed: {
    title: 'Recording Stopped',
    message:
      'Recording stopped because audio capture failed to start. Resume to keep recording.',
  },
  audio_start_exhausted: {
    title: 'Recording Stopped',
    message:
      'Recording stopped because audio capture failed to start after multiple attempts. Resume to keep recording.',
  },
  audio_start_no_controller: {
    title: 'Recording Stopped',
    message:
      "Recording stopped because the audio device couldn't be accessed. Make sure you're on macOS 14.2 or later, then resume recording.",
  },
  audio_start_invalid_state: {
    title: 'Recording Stopped',
    message:
      'Recording stopped because audio capture hit an unexpected state. Resume to keep recording.',
  },
  audio_start_permission_denied: {
    title: 'Allow System Audio Recording',
    message:
      'Littlebird needs System Audio Recording access to take meeting notes. Turn on Littlebird under System Audio Recording Only, then resume recording.',
  },
  ipc_timeout: {
    title: 'Recording Stopped',
    message:
      'Recording stopped because the recording service stopped responding. Resume to keep recording.',
  },
  silent_failure_exhausted: {
    title: 'Recording Stopped',
    message:
      "Recording stopped because transcription failed and couldn't be recovered. Resume to keep recording.",
  },
  meeting_quota_exceeded: {
    title: 'Meeting Limit Reached',
    message:
      "You've reached the meeting limit on the free plan. Upgrade to Plus for unlimited meetings.",
  },
};

/**
 * Handles unrecoverable meeting interrupt events from ContextKit.
 * Stops the meeting and notifies the user when recovery has been exhausted.
 */
export function useMeetingInterrupt() {
  const recordingStatus = meetingStore.recording.status;
  const handledRef = useRef(false);

  // Reset flag on loading AND recording transitions. This means the flag resets
  // twice per meeting start (loading → recording), but that's fine: the only
  // interrupt that can arrive during 'loading' is quota_exceeded, which
  // immediately stops the meeting and won't need deduplication, because it's only
  // sent once.
  useEffect(() => {
    if (recordingStatus === 'recording' || recordingStatus === 'loading') {
      handledRef.current = false;
    }
  }, [recordingStatus]);

  useEffect(() => {
    const unlisten = electronApis.meetings.listenForMeetingInterrupt(
      async ({ meetingId, reason }) => {
        meetingDebugStore.record({
          kind: 'interrupt',
          meetingId: meetingId ?? undefined,
          summary: `Interrupted: ${reason}`,
          fields: { reason, recordingStatus },
        });
        if (recordingStatus !== 'recording' && recordingStatus !== 'loading') {
          return;
        }
        if (handledRef.current) return;
        handledRef.current = true;

        const activeMeetingId =
          meetingStore.asRecordingOrStoppingMeeting?.meetingId ?? meetingId;

        logError(
          new Error(`Meeting interrupted: ${reason}`),
          '[meeting-interrupt]',
          { metadata: { meetingId, reason } }
        );

        const { title, message } = INTERRUPT_REASON_MESSAGES[reason] ?? {
          title: 'Recording Stopped',
          message:
            'Recording stopped because of an unexpected error. Resume to keep recording.',
        };

        alertUnexpectedRecordingStop({
          meetingId: activeMeetingId,
          cause: 'interrupted',
          interruptReason: reason,
          title,
          message,
        });

        if (reason === 'audio_start_permission_denied') {
          electronApis.system
            .openSystemAudioRecordingSettings()
            .catch((error) =>
              logError(
                error,
                '[meeting-interrupt] Failed to open System Audio Recording settings'
              )
            );
        }

        const isAudioStartFailure =
          reason === 'audio_start_failed' ||
          reason === 'audio_start_permission_denied';
        const stopReason = isAudioStartFailure
          ? 'audio_start_failed'
          : 'transcriber_interrupt';

        meetingApis.enqueueStopMeeting({ type: stopReason }).catch((error) => {
          logError(error, '[meeting-interrupt] Failed to stop meeting');
        });
      }
    );

    return unlisten;
  }, [recordingStatus]);
}

/** Interval between heartbeat polls to ContextKit */
export const HEARTBEAT_INTERVAL_MS = 1000;
/** Number of missed heartbeats to tolerate before timeout */
export const HEARTBEAT_SKIP_TOLERANCE = 4;
/** Max wait time for a heartbeat response before declaring failure */
export const HEARTBEAT_TIMEOUT_MS =
  HEARTBEAT_INTERVAL_MS * (1 + HEARTBEAT_SKIP_TOLERANCE);

export const HEARTBEAT_FAILURE_LOG =
  '[meeting-heartbeat] ContextKit unresponsive';

export const HEARTBEAT_SESSION_ENDED_LOG =
  '[meeting-heartbeat] ContextKit session ended or meeting ID mismatch';

export interface HeartbeatFailureInput {
  meetingId: string | undefined;
  lastHealth: MeetingHeartbeatHealth | undefined;
  recordingStartedAt: Date | undefined;
  appName: string | undefined;
  error: Error;
}

export function buildHeartbeatFailureTelemetry(input: HeartbeatFailureInput) {
  const { meetingId, lastHealth, recordingStartedAt, appName, error } = input;
  const now = new Date();
  const meetingDurationMs = recordingStartedAt
    ? now.getTime() - recordingStartedAt.getTime()
    : 0;

  return {
    logPayload: {
      meetingId,
      meetingDuration: formatDuration(meetingDurationMs),
      lastHealthIsActive: lastHealth?.isActive,
      lastHealthTranscriberState: lastHealth?.transcriberState,
      secondsSinceTranscription: lastHealth?.secondsSinceLastTranscription,
      appName,
      error: error.message,
    },
  };
}

/**
 * Polls ContextKit meeting heartbeat during active recording.
 *
 * Logs invariant violations via console.warn without stopping the meeting.
 */
export function useMeetingHeartbeat() {
  // React Compiler would cache the effect's MobX deps, so a later recording never starts it
  'use no memo';
  const recordingStatus = meetingStore.recording.status;
  const meetingId = meetingStore.asRecordingOrStoppingMeeting?.meetingId;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const lastHealthRef = useRef<MeetingHeartbeatHealth | undefined>(undefined);

  useEffect(() => {
    let aborted = false;

    const clearHeartbeat = () => {
      aborted = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };

    if (recordingStatus !== 'recording') {
      clearHeartbeat();
      lastHealthRef.current = undefined;
      meetingDebugStore.setHeartbeatHealth(undefined);
      return;
    }

    const scheduleNext = () => {
      if (aborted) return;
      timeoutRef.current = setTimeout(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    };

    const sendHeartbeat = async () => {
      try {
        const health = await withTimeout(
          electronApis.meetings.meetingHeartbeat(),
          HEARTBEAT_TIMEOUT_MS
        );
        if (aborted) return;
        lastHealthRef.current = health;
        meetingDebugStore.setHeartbeatHealth(health);

        const hasMeetingIdMismatch = Boolean(
          meetingId &&
          health.meetingId &&
          health.meetingId.toLowerCase() !== meetingId.toLowerCase()
        );

        if (!health.isActive || hasMeetingIdMismatch) {
          console.warn(HEARTBEAT_SESSION_ENDED_LOG, {
            healthIsActive: health.isActive,
            healthMeetingId: health.meetingId,
            expectedActiveMeetingId: meetingId,
          });
          clearHeartbeat();
          return;
        }

        scheduleNext();
      } catch (error) {
        if (aborted) return;

        const err = error instanceof Error ? error : new Error(String(error));

        logError(err, HEARTBEAT_FAILURE_LOG);
        clearHeartbeat();

        const telemetry = buildHeartbeatFailureTelemetry({
          meetingId: meetingId ?? undefined,
          lastHealth: lastHealthRef.current,
          recordingStartedAt:
            meetingStore.asRecordingOrStoppingMeeting?.startedAt,
          appName: meetingId
            ? (getMeeting(meetingId)?.source_app ?? undefined)
            : undefined,
          error: err,
        });

        console.warn('[meeting-heartbeat]', telemetry.logPayload);
      }
    };

    // Reset for new session
    lastHealthRef.current = undefined;

    sendHeartbeat();

    return clearHeartbeat;
  }, [recordingStatus, meetingId]);
}

type AgendaItemStatus =
  | 'starting'
  | 'recording'
  | 'notes'
  | 'prep-ready'
  | 'upcoming';

export type AgendaItem = {
  key: string;
  dayKey: string;
  title: string;
  startsAt: Date;
  endsAt: Date | null;
  status: AgendaItemStatus;
  meetingId?: string;
  event?: CalendarEvent;
  existingMeeting?: Meeting;
  hasCalendarEvent: boolean;
  projectId: string | null;
  attendeesLabel: string | null;
  joinUrl: string | null;
};

const toAgendaDayKey = (date: Date) => format(date, 'yyyy-MM-dd');

const toDateOrNull = (value: Date | string | null | undefined) =>
  value ? new Date(value) : null;

export const isNewNoteDisabled = () =>
  meetingStore.isStartingMeeting || meetingStore.recording.status !== 'idle';

export const startMeetingFromOverview = () => {
  if (!inElectron()) {
    triggerDeepLink('/meetings', { params: { start: 'true' } });
    return;
  }

  if (meetingStore.isStartingMeeting) return;
  meetingStore.meetingStartedFromOverview = true;
  meetingStore.setIsStartingMeeting(true);
  withUIDelay(async () => {
    try {
      await meetingApis.enqueueStartMeeting({
        payload: { startReason: 'manual' },
      });
    } finally {
      meetingStore.setIsStartingMeeting(false);
    }
  });
};

const MINUTE_MS = 60_000;
const NEXT_UP_WINDOW_MS = 24 * 60 * 60_000;

type MeetingsAgenda = {
  live: AgendaItem[];
  nextItem: AgendaItem | undefined;
  projectIds: string[];
  hasUnassigned: boolean;
};

const EMPTY_CALENDAR_EVENTS: CalendarEvent[] = [];

const EMPTY_MEETING_CIRCLES: MeetingCircles = {
  people: [],
  recurring: [],
  projectIds: [],
  hasUnassignedMeetings: false,
};

const useMeetingCirclesResult = () => {
  'use no memo';
  const { meetings } = useStores();
  return meetingApis.useMeetingCirclesQuery({
    accountId: userStore.user?.id ?? null,
    currentUserEmail: userStore.user?.email ?? null,
    meetingsVersion: String(meetings.groupingRevision),
  });
};

export const useMeetingCircles = (): MeetingCircles => {
  'use no memo';
  const userConfig = useUserConfig();
  return withoutHiddenCircles(
    useMeetingCirclesResult().data ?? EMPTY_MEETING_CIRCLES,
    userConfig.meetings.get('hidden_circles') ?? []
  );
};

export const useSelectedCircle = (): MeetingCircle | null => {
  'use no memo';
  const { data, isSuccess, isPlaceholderData } = useMeetingCirclesResult();
  const userConfig = useUserConfig();
  const circles = withoutHiddenCircles(
    data ?? EMPTY_MEETING_CIRCLES,
    userConfig.meetings.get('hidden_circles') ?? []
  );
  const { circleKey } = meetingsSidebarStore;
  const circle = circleKey
    ? ([...circles.recurring, ...circles.people].find(
        (candidate) => candidate.key === circleKey
      ) ?? null)
    : null;
  const isMissing =
    circleKey !== null && circle === null && isSuccess && !isPlaceholderData;

  useEffect(() => {
    if (isMissing) meetingsSidebarStore.clearSlice();
  }, [isMissing]);

  return circle;
};

export const useHideMeetingCircle = (): ((circle: MeetingCircle) => void) => {
  'use no memo';
  const userConfig = useUserConfig();

  return useCallback(
    (circle) => {
      const current = userConfig.meetings.get('hidden_circles') ?? [];
      userConfig.meetings.set(
        'hidden_circles',
        withCircleHidden(current, circle.key, true)
      );

      toast(`Hid ${circle.name}`, {
        action: {
          label: 'Undo',
          onClick: () => {
            const currentHiddenKeys =
              userConfig.meetings.get('hidden_circles') ?? [];
            userConfig.meetings.set(
              'hidden_circles',
              withCircleHidden(currentHiddenKeys, circle.key, false)
            );
          },
        },
      });
    },
    [userConfig]
  );
};

export const useHiddenMeetingCircles = (): { key: string; name: string }[] => {
  'use no memo';
  const userConfig = useUserConfig();
  const circles = useMeetingCirclesResult().data ?? EMPTY_MEETING_CIRCLES;
  const allCircles = [...circles.recurring, ...circles.people];

  return (userConfig.meetings.get('hidden_circles') ?? []).map((key) => {
    const circle = allCircles.find((candidate) => candidate.key === key);
    const fallbackName = key.startsWith('series:')
      ? 'Recurring meeting'
      : key.split(',').join(', ');

    return {
      key,
      name: circle?.name.trim() ? circle.name : fallbackName,
    };
  });
};

export const useUnhideMeetingCircle = (): ((key: string) => void) => {
  'use no memo';
  const userConfig = useUserConfig();

  return useCallback(
    (key) => {
      const current = userConfig.meetings.get('hidden_circles') ?? [];
      userConfig.meetings.set(
        'hidden_circles',
        withCircleHidden(current, key, false)
      );
    },
    [userConfig]
  );
};

const CIRCLE_PAGE_SIZE = 50;

export const useCircleMeetingsLoad = (
  circle: MeetingCircle | null,
  loadAll: boolean
) => {
  const { meetings } = useStores();
  const circleKey = circle?.key ?? null;
  const [page, setPage] = useState({ circleKey, limit: CIRCLE_PAGE_SIZE });
  const limit = page.circleKey === circleKey ? page.limit : CIRCLE_PAGE_SIZE;
  const meetingIds = circle?.meetingIds.slice(0, limit) ?? [];

  const { isPending } = meetingApis.useMeetingsByIdsQuery(
    circleKey,
    loadAll ? (circle?.meetingIds ?? []) : meetingIds
  );

  return {
    isLoading: circle !== null && isPending,
    meetingIds,
    hasMore: circle !== null && limit < circle.meetingIds.length,
    loadMore: async () => {
      if (!circle) return;
      const nextLimit = limit + CIRCLE_PAGE_SIZE;
      await meetings.loadByIds(circle.meetingIds.slice(limit, nextLimit));
      setPage({ circleKey, limit: nextLimit });
    },
  };
};

const EMPTY_AGENDA: MeetingsAgenda = {
  live: [],
  nextItem: undefined,
  projectIds: [],
  hasUnassigned: false,
};

const buildAgenda = (
  calendarEvents: CalendarEvent[],
  minute: number
): MeetingsAgenda => {
  const upcomingMeetingsWithStatus = buildUpcomingMeetingsWithStatus(
    calendarEvents,
    new Date(minute * MINUTE_MS)
  ).filter(
    ({ event }) => !config.meetings.exclude_solo_events || !isSoloEvent(event)
  );
  const { meetings: meetingsStore, projects } = getStores();
  const currentUserEmail = userStore.user?.email;
  const projectIdFor = (meetingId?: string) =>
    (meetingId && projects.getProjectForMeeting(meetingId)?.id) || null;
  const attendeesLabelFor = (
    eventAttendees: CalendarEvent['attendees'] | undefined,
    meetingAttendees: Meeting['attendees'] | undefined
  ) => {
    const attendees = getOtherAttendees(
      getDisplayAttendees(eventAttendees, meetingAttendees),
      currentUserEmail
    );
    return attendees.length > 0 ? formatAttendees(attendees) : null;
  };
  const eventsById = new Map(calendarEvents.map((event) => [event.id, event]));
  const upcomingItems: AgendaItem[] = upcomingMeetingsWithStatus.map(
    ({ event, meeting }) => {
      const storeMeeting = meeting ? meetingsStore.get(meeting.id) : undefined;
      const startsAt = new Date(event.start_datetime);
      const endsAt = toDateOrNull(event.end_datetime);
      return {
        key: `event:${event.id}`,
        dayKey: toAgendaDayKey(startsAt),
        title: event.summary || 'Untitled event',
        startsAt,
        endsAt,
        status:
          storeMeeting?.prepStatus === 'completed' ? 'prep-ready' : 'upcoming',
        event,
        existingMeeting: meeting,
        hasCalendarEvent: true,
        projectId: projectIdFor(meeting?.id),
        attendeesLabel: attendeesLabelFor(event.attendees, meeting?.attendees),
        joinUrl: withAuthUser(getMeetingLink(event), event) ?? null,
      };
    }
  );

  const live: AgendaItem[] = meetingsStore.orderedData
    .filter((storeMeeting) =>
      meetingStore.isMeetingIdRecording(storeMeeting.id)
    )
    .map((storeMeeting) => {
      const event = storeMeeting.eventId
        ? eventsById.get(storeMeeting.eventId)
        : undefined;
      return {
        key: `meeting:${storeMeeting.id}`,
        dayKey: toAgendaDayKey(new Date()),
        title: storeMeeting.name ?? PLACEHOLDER_MEETING_TITLE,
        startsAt:
          meetingStore.recording.status === 'recording'
            ? new Date(meetingStore.recording.startedAt)
            : new Date(),
        endsAt: null,
        status: 'recording',
        meetingId: storeMeeting.id,
        hasCalendarEvent: Boolean(storeMeeting.eventId),
        projectId: projectIdFor(storeMeeting.id),
        attendeesLabel: attendeesLabelFor(
          event?.attendees,
          storeAttendeesToDisplayAttendees(storeMeeting.attendees)
        ),
        joinUrl: null,
      };
    });

  return {
    live,
    nextItem: minBy(
      upcomingItems.filter(
        (item) =>
          !isAllDayEvent(item.event) &&
          item.startsAt.getTime() <= minute * MINUTE_MS + NEXT_UP_WINDOW_MS
      ),
      (item) => item.startsAt.getTime()
    ),
    projectIds: uniq(
      upcomingItems.flatMap((item) => (item.projectId ? [item.projectId] : []))
    ),
    hasUnassigned: upcomingItems.some((item) => !item.projectId),
  };
};

const ALL_DAY_MS = 24 * 60 * 60_000;

const isAllDayEvent = (event: CalendarEvent | undefined) =>
  Boolean(
    event &&
    (event.is_all_day ||
      new Date(event.end_datetime).getTime() -
        new Date(event.start_datetime).getTime() >=
        ALL_DAY_MS)
  );

const computedForLastArgs = <Args extends unknown[], Result>(
  build: (...args: Args) => Result
) => {
  let last: { args: Args; value: IComputedValue<Result> } | null = null;
  return (...args: Args) => {
    if (!last || args.some((arg, index) => arg !== last?.args[index])) {
      last = { args, value: computed(() => build(...args)) };
    }
    return last.value.get();
  };
};

const getAgenda = computedForLastArgs(buildAgenda);

const currentMinute = () => Math.floor(Date.now() / MINUTE_MS);

export const useMeetingsAgenda = (): MeetingsAgenda => {
  const { data: calendarEvents = EMPTY_CALENDAR_EVENTS } =
    meetingApis.useCalendarEventsQuery();
  const [minute, setMinute] = useState(currentMinute);

  useEffect(() => {
    setMinute(currentMinute());
    const interval = setInterval(() => setMinute(currentMinute()), MINUTE_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!hasSDK()) return;
    getStores()
      .meetings.ensureListLoadedFromStorage()
      .catch((error) => {
        logError(error, 'Failed to hydrate meetings for the agenda sidebar', {
          context: 'useMeetingsAgenda:ensureListLoadedFromStorage',
        });
      });
  }, []);

  if (!hasSDK()) return EMPTY_AGENDA;
  return getAgenda(calendarEvents, minute);
};

export const useOpenMeetingsLanding = () => {
  const navigate = useNavigate();

  return () => {
    meetingStore.setMeetingNotesOnboarded(true);
    meetingsSidebarStore.clearSlice();
    return navigate({ to: '/meetings' });
  };
};
